import { useState, useEffect } from 'react';
import {
  AlertTriangle,
  Shield,
  TrendingUp,
  Users,
  Search,
  Plus,
  RefreshCw,
  Eye,
  Edit,
  Trash2,
  AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  riskManagementApi,
  RiskProfile,
  RiskDashboard,
  CreateRiskProfileDto,
  UpdateRiskProfileDto,
} from '@/api/services/risk-management.api';
import { inventoryApi, InventoryItem } from '@/api/services/inventory.api';

function getRiskBadgeVariant(risk: number): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (risk >= 8) return 'destructive';
  if (risk >= 6) return 'default';
  if (risk >= 4) return 'secondary';
  return 'outline';
}

function getRiskLabel(risk: number): string {
  if (risk >= 8) return 'Critical';
  if (risk >= 6) return 'High';
  if (risk >= 4) return 'Medium';
  return 'Low';
}

function getRiskColor(risk: number): string {
  if (risk >= 8) return 'text-red-600 bg-red-100 dark:bg-red-900/30';
  if (risk >= 6) return 'text-orange-600 bg-orange-100 dark:bg-orange-900/30';
  if (risk >= 4) return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30';
  return 'text-green-600 bg-green-100 dark:bg-green-900/30';
}

export default function RiskManagement() {
  const [dashboard, setDashboard] = useState<RiskDashboard | null>(null);
  const [profiles, setProfiles] = useState<RiskProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [riskFilter, setRiskFilter] = useState<string>('all');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<RiskProfile | null>(null);

  // Form state
  const [formData, setFormData] = useState<CreateRiskProfileDto>({
    itemId: '',
    supplyRisk: 5,
    demandRisk: 5,
    qualityRisk: 5,
    priceRisk: 5,
    obsolescenceRisk: 5,
    singleSourced: false,
    longLeadTime: false,
    highValue: false,
    criticalItem: false,
    volatilePricing: false,
    safetyStockDays: 30,
  });

  // Items for selection
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);

  useEffect(() => {
    fetchData();
  }, [pagination.page, searchQuery, riskFilter]);

  useEffect(() => {
    if (showCreateModal) {
      fetchInventoryItems();
    }
  }, [showCreateModal]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [dashboardRes, profilesRes] = await Promise.all([
        riskManagementApi.getDashboard(),
        riskManagementApi.getAll({
          page: pagination.page,
          limit: pagination.limit,
          search: searchQuery || undefined,
          minRisk: riskFilter === 'high' ? 7 : riskFilter === 'medium' ? 4 : undefined,
          maxRisk: riskFilter === 'low' ? 3 : riskFilter === 'medium' ? 6 : undefined,
        }),
      ]);

      setDashboard(dashboardRes.data);
      setProfiles(profilesRes.data.data);
      setPagination((prev) => ({
        ...prev,
        total: profilesRes.data.pagination.total,
        totalPages: profilesRes.data.pagination.totalPages,
      }));
    } catch (error) {
      toast.error('Failed to load risk data');
    } finally {
      setLoading(false);
    }
  };

  const fetchInventoryItems = async () => {
    try {
      setLoadingItems(true);
      const response = await inventoryApi.getAll({ limit: 100 });
      setInventoryItems(response.data);
    } catch (error) {
      toast.error('Failed to load inventory items');
    } finally {
      setLoadingItems(false);
    }
  };

  const handleCreate = async () => {
    try {
      if (!formData.itemId) {
        toast.error('Please select an inventory item');
        return;
      }

      await riskManagementApi.create(formData);
      toast.success('Risk profile created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create risk profile');
    }
  };

  const handleUpdate = async () => {
    if (!selectedProfile) return;

    try {
      const updateData: UpdateRiskProfileDto = {
        supplyRisk: formData.supplyRisk,
        demandRisk: formData.demandRisk,
        qualityRisk: formData.qualityRisk,
        priceRisk: formData.priceRisk,
        obsolescenceRisk: formData.obsolescenceRisk,
        singleSourced: formData.singleSourced,
        longLeadTime: formData.longLeadTime,
        highValue: formData.highValue,
        criticalItem: formData.criticalItem,
        volatilePricing: formData.volatilePricing,
        mitigationStrategy: formData.mitigationStrategy,
        safetyStockDays: formData.safetyStockDays,
        notes: formData.notes,
      };

      await riskManagementApi.update(selectedProfile.id, updateData);
      toast.success('Risk profile updated successfully');
      setShowEditModal(false);
      setSelectedProfile(null);
      resetForm();
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update risk profile');
    }
  };

  const handleRunAssessment = async (profile: RiskProfile) => {
    try {
      await riskManagementApi.runAssessment(profile.id);
      toast.success('Risk assessment completed');
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to run assessment');
    }
  };

  const handleDelete = async (profile: RiskProfile) => {
    if (!confirm(`Are you sure you want to delete the risk profile for "${profile.item?.name}"?`)) {
      return;
    }

    try {
      await riskManagementApi.delete(profile.id);
      toast.success('Risk profile deleted successfully');
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete risk profile');
    }
  };

  const openEditModal = (profile: RiskProfile) => {
    setSelectedProfile(profile);
    setFormData({
      itemId: profile.itemId,
      supplyRisk: profile.supplyRisk,
      demandRisk: profile.demandRisk,
      qualityRisk: profile.qualityRisk,
      priceRisk: profile.priceRisk,
      obsolescenceRisk: profile.obsolescenceRisk,
      singleSourced: profile.singleSourced,
      longLeadTime: profile.longLeadTime,
      highValue: profile.highValue,
      criticalItem: profile.criticalItem,
      volatilePricing: profile.volatilePricing,
      mitigationStrategy: profile.mitigationStrategy,
      safetyStockDays: profile.safetyStockDays,
      notes: profile.notes,
    });
    setShowEditModal(true);
  };

  const openDetailModal = (profile: RiskProfile) => {
    setSelectedProfile(profile);
    setShowDetailModal(true);
  };

  const resetForm = () => {
    setFormData({
      itemId: '',
      supplyRisk: 5,
      demandRisk: 5,
      qualityRisk: 5,
      priceRisk: 5,
      obsolescenceRisk: 5,
      singleSourced: false,
      longLeadTime: false,
      highValue: false,
      criticalItem: false,
      volatilePricing: false,
      safetyStockDays: 30,
    });
  };

  if (loading && !dashboard) {
    return (
      <div className="space-y-6 p-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Risk Management</h1>
          <p className="text-sm text-muted-foreground">
            Monitor and manage supply chain risks for inventory items
          </p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Risk Profile
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">High Risk Items</CardTitle>
            <AlertTriangle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboard?.highRiskCount || 0}</div>
            <p className="text-xs text-muted-foreground">Risk score ≥ 7</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Single-Sourced</CardTitle>
            <Users className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboard?.singleSourcedCount || 0}</div>
            <p className="text-xs text-muted-foreground">Items with one supplier</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Critical Items</CardTitle>
            <Shield className="h-4 w-4 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboard?.criticalItemCount || 0}</div>
            <p className="text-xs text-muted-foreground">Business-critical inventory</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Long Lead Time</CardTitle>
            <TrendingUp className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboard?.longLeadTimeCount || 0}</div>
            <p className="text-xs text-muted-foreground">Extended procurement time</p>
          </CardContent>
        </Card>
      </div>

      {/* Risk Distribution */}
      {dashboard?.riskDistribution && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Risk Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <div className="flex h-4 rounded-full overflow-hidden">
                  {dashboard.riskDistribution.low > 0 && (
                    <div
                      className="bg-green-500"
                      style={{
                        width: `${(dashboard.riskDistribution.low / dashboard.totalProfiles) * 100}%`,
                      }}
                    />
                  )}
                  {dashboard.riskDistribution.medium > 0 && (
                    <div
                      className="bg-yellow-500"
                      style={{
                        width: `${(dashboard.riskDistribution.medium / dashboard.totalProfiles) * 100}%`,
                      }}
                    />
                  )}
                  {dashboard.riskDistribution.high > 0 && (
                    <div
                      className="bg-orange-500"
                      style={{
                        width: `${(dashboard.riskDistribution.high / dashboard.totalProfiles) * 100}%`,
                      }}
                    />
                  )}
                  {dashboard.riskDistribution.critical > 0 && (
                    <div
                      className="bg-red-500"
                      style={{
                        width: `${(dashboard.riskDistribution.critical / dashboard.totalProfiles) * 100}%`,
                      }}
                    />
                  )}
                </div>
                <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded bg-green-500" />
                    Low ({dashboard.riskDistribution.low})
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded bg-yellow-500" />
                    Medium ({dashboard.riskDistribution.medium})
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded bg-orange-500" />
                    High ({dashboard.riskDistribution.high})
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded bg-red-500" />
                    Critical ({dashboard.riskDistribution.critical})
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search items..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={riskFilter} onValueChange={setRiskFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by risk" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Risk Levels</SelectItem>
            <SelectItem value="low">Low (1-3)</SelectItem>
            <SelectItem value="medium">Medium (4-6)</SelectItem>
            <SelectItem value="high">High (7-10)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Risk Profiles Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item</TableHead>
                <TableHead>SKU</TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="text-center">Overall Risk</TableHead>
                <TableHead className="text-center">Supply</TableHead>
                <TableHead className="text-center">Demand</TableHead>
                <TableHead>Risk Factors</TableHead>
                <TableHead>Last Assessed</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {profiles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8">
                    <AlertCircle className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                    <p className="text-muted-foreground">No risk profiles found</p>
                  </TableCell>
                </TableRow>
              ) : (
                profiles.map((profile) => (
                  <TableRow key={profile.id}>
                    <TableCell className="font-medium">
                      {profile.item?.name || 'Unknown'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {profile.item?.sku}
                    </TableCell>
                    <TableCell>{profile.item?.category || '-'}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant={getRiskBadgeVariant(profile.overallRisk)}>
                        {profile.overallRisk}/10 - {getRiskLabel(profile.overallRisk)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getRiskColor(profile.supplyRisk)}`}>
                        {profile.supplyRisk}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getRiskColor(profile.demandRisk)}`}>
                        {profile.demandRisk}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {profile.singleSourced && (
                          <Badge variant="outline" className="text-xs">Single Source</Badge>
                        )}
                        {profile.criticalItem && (
                          <Badge variant="outline" className="text-xs">Critical</Badge>
                        )}
                        {profile.longLeadTime && (
                          <Badge variant="outline" className="text-xs">Long Lead</Badge>
                        )}
                        {profile.highValue && (
                          <Badge variant="outline" className="text-xs">High Value</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {profile.lastAssessedAt
                        ? new Date(profile.lastAssessedAt).toLocaleDateString()
                        : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openDetailModal(profile)}
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRunAssessment(profile)}
                          title="Run Assessment"
                        >
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditModal(profile)}
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(profile)}
                          title="Delete"
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
            {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
            {pagination.total} results
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page === 1}
              onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page === pagination.totalPages}
              onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Risk Profile</DialogTitle>
            <DialogDescription>
              Add a new risk profile for an inventory item
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Item Selection */}
            <div className="space-y-2">
              <Label>Inventory Item *</Label>
              <Select
                value={formData.itemId}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, itemId: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder={loadingItems ? 'Loading...' : 'Select item'} />
                </SelectTrigger>
                <SelectContent>
                  {inventoryItems.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name} ({item.sku})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Risk Scores */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Supply Risk: {formData.supplyRisk}</Label>
                <Slider
                  value={[formData.supplyRisk || 5]}
                  min={1}
                  max={10}
                  step={1}
                  onValueChange={([value]) =>
                    setFormData((prev) => ({ ...prev, supplyRisk: value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Demand Risk: {formData.demandRisk}</Label>
                <Slider
                  value={[formData.demandRisk || 5]}
                  min={1}
                  max={10}
                  step={1}
                  onValueChange={([value]) =>
                    setFormData((prev) => ({ ...prev, demandRisk: value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Quality Risk: {formData.qualityRisk}</Label>
                <Slider
                  value={[formData.qualityRisk || 5]}
                  min={1}
                  max={10}
                  step={1}
                  onValueChange={([value]) =>
                    setFormData((prev) => ({ ...prev, qualityRisk: value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Price Risk: {formData.priceRisk}</Label>
                <Slider
                  value={[formData.priceRisk || 5]}
                  min={1}
                  max={10}
                  step={1}
                  onValueChange={([value]) =>
                    setFormData((prev) => ({ ...prev, priceRisk: value }))
                  }
                />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Obsolescence Risk: {formData.obsolescenceRisk}</Label>
                <Slider
                  value={[formData.obsolescenceRisk || 5]}
                  min={1}
                  max={10}
                  step={1}
                  onValueChange={([value]) =>
                    setFormData((prev) => ({ ...prev, obsolescenceRisk: value }))
                  }
                />
              </div>
            </div>

            {/* Risk Factors */}
            <div className="space-y-4">
              <Label className="text-base">Risk Factors</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="singleSourced">Single Sourced</Label>
                  <Switch
                    id="singleSourced"
                    checked={formData.singleSourced}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({ ...prev, singleSourced: checked }))
                    }
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="criticalItem">Critical Item</Label>
                  <Switch
                    id="criticalItem"
                    checked={formData.criticalItem}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({ ...prev, criticalItem: checked }))
                    }
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="longLeadTime">Long Lead Time</Label>
                  <Switch
                    id="longLeadTime"
                    checked={formData.longLeadTime}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({ ...prev, longLeadTime: checked }))
                    }
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="highValue">High Value</Label>
                  <Switch
                    id="highValue"
                    checked={formData.highValue}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({ ...prev, highValue: checked }))
                    }
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="volatilePricing">Volatile Pricing</Label>
                  <Switch
                    id="volatilePricing"
                    checked={formData.volatilePricing}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({ ...prev, volatilePricing: checked }))
                    }
                  />
                </div>
              </div>
            </div>

            {/* Safety Stock */}
            <div className="space-y-2">
              <Label>Safety Stock Days: {formData.safetyStockDays}</Label>
              <Slider
                value={[formData.safetyStockDays || 30]}
                min={0}
                max={180}
                step={5}
                onValueChange={([value]) =>
                  setFormData((prev) => ({ ...prev, safetyStockDays: value }))
                }
              />
            </div>

            {/* Mitigation & Notes */}
            <div className="space-y-2">
              <Label>Mitigation Strategy</Label>
              <Textarea
                placeholder="Describe mitigation strategies for this item..."
                value={formData.mitigationStrategy || ''}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, mitigationStrategy: e.target.value }))
                }
              />
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Additional notes..."
                value={formData.notes || ''}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, notes: e.target.value }))
                }
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate}>Create Profile</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Risk Profile</DialogTitle>
            <DialogDescription>
              Update risk assessment for {selectedProfile?.item?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Risk Scores */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Supply Risk: {formData.supplyRisk}</Label>
                <Slider
                  value={[formData.supplyRisk || 5]}
                  min={1}
                  max={10}
                  step={1}
                  onValueChange={([value]) =>
                    setFormData((prev) => ({ ...prev, supplyRisk: value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Demand Risk: {formData.demandRisk}</Label>
                <Slider
                  value={[formData.demandRisk || 5]}
                  min={1}
                  max={10}
                  step={1}
                  onValueChange={([value]) =>
                    setFormData((prev) => ({ ...prev, demandRisk: value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Quality Risk: {formData.qualityRisk}</Label>
                <Slider
                  value={[formData.qualityRisk || 5]}
                  min={1}
                  max={10}
                  step={1}
                  onValueChange={([value]) =>
                    setFormData((prev) => ({ ...prev, qualityRisk: value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Price Risk: {formData.priceRisk}</Label>
                <Slider
                  value={[formData.priceRisk || 5]}
                  min={1}
                  max={10}
                  step={1}
                  onValueChange={([value]) =>
                    setFormData((prev) => ({ ...prev, priceRisk: value }))
                  }
                />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Obsolescence Risk: {formData.obsolescenceRisk}</Label>
                <Slider
                  value={[formData.obsolescenceRisk || 5]}
                  min={1}
                  max={10}
                  step={1}
                  onValueChange={([value]) =>
                    setFormData((prev) => ({ ...prev, obsolescenceRisk: value }))
                  }
                />
              </div>
            </div>

            {/* Risk Factors */}
            <div className="space-y-4">
              <Label className="text-base">Risk Factors</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="edit-singleSourced">Single Sourced</Label>
                  <Switch
                    id="edit-singleSourced"
                    checked={formData.singleSourced}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({ ...prev, singleSourced: checked }))
                    }
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="edit-criticalItem">Critical Item</Label>
                  <Switch
                    id="edit-criticalItem"
                    checked={formData.criticalItem}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({ ...prev, criticalItem: checked }))
                    }
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="edit-longLeadTime">Long Lead Time</Label>
                  <Switch
                    id="edit-longLeadTime"
                    checked={formData.longLeadTime}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({ ...prev, longLeadTime: checked }))
                    }
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="edit-highValue">High Value</Label>
                  <Switch
                    id="edit-highValue"
                    checked={formData.highValue}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({ ...prev, highValue: checked }))
                    }
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="edit-volatilePricing">Volatile Pricing</Label>
                  <Switch
                    id="edit-volatilePricing"
                    checked={formData.volatilePricing}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({ ...prev, volatilePricing: checked }))
                    }
                  />
                </div>
              </div>
            </div>

            {/* Safety Stock */}
            <div className="space-y-2">
              <Label>Safety Stock Days: {formData.safetyStockDays}</Label>
              <Slider
                value={[formData.safetyStockDays || 30]}
                min={0}
                max={180}
                step={5}
                onValueChange={([value]) =>
                  setFormData((prev) => ({ ...prev, safetyStockDays: value }))
                }
              />
            </div>

            {/* Mitigation & Notes */}
            <div className="space-y-2">
              <Label>Mitigation Strategy</Label>
              <Textarea
                placeholder="Describe mitigation strategies for this item..."
                value={formData.mitigationStrategy || ''}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, mitigationStrategy: e.target.value }))
                }
              />
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Additional notes..."
                value={formData.notes || ''}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, notes: e.target.value }))
                }
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdate}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Risk Profile Details</DialogTitle>
            <DialogDescription>
              {selectedProfile?.item?.name} ({selectedProfile?.item?.sku})
            </DialogDescription>
          </DialogHeader>

          {selectedProfile && (
            <div className="space-y-6 py-4">
              {/* Overall Risk */}
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <span className="font-medium">Overall Risk Score</span>
                <Badge
                  variant={getRiskBadgeVariant(selectedProfile.overallRisk)}
                  className="text-lg px-4 py-1"
                >
                  {selectedProfile.overallRisk}/10 - {getRiskLabel(selectedProfile.overallRisk)}
                </Badge>
              </div>

              {/* Risk Breakdown */}
              <div className="space-y-3">
                <h4 className="font-medium">Risk Breakdown</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Supply Risk</span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getRiskColor(selectedProfile.supplyRisk)}`}>
                      {selectedProfile.supplyRisk}/10
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Demand Risk</span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getRiskColor(selectedProfile.demandRisk)}`}>
                      {selectedProfile.demandRisk}/10
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Quality Risk</span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getRiskColor(selectedProfile.qualityRisk)}`}>
                      {selectedProfile.qualityRisk}/10
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Price Risk</span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getRiskColor(selectedProfile.priceRisk)}`}>
                      {selectedProfile.priceRisk}/10
                    </span>
                  </div>
                  <div className="flex justify-between items-center col-span-2">
                    <span className="text-sm text-muted-foreground">Obsolescence Risk</span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getRiskColor(selectedProfile.obsolescenceRisk)}`}>
                      {selectedProfile.obsolescenceRisk}/10
                    </span>
                  </div>
                </div>
              </div>

              {/* Risk Factors */}
              <div className="space-y-3">
                <h4 className="font-medium">Risk Factors</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedProfile.singleSourced && (
                    <Badge variant="outline">Single Sourced</Badge>
                  )}
                  {selectedProfile.criticalItem && (
                    <Badge variant="outline">Critical Item</Badge>
                  )}
                  {selectedProfile.longLeadTime && (
                    <Badge variant="outline">Long Lead Time</Badge>
                  )}
                  {selectedProfile.highValue && (
                    <Badge variant="outline">High Value</Badge>
                  )}
                  {selectedProfile.volatilePricing && (
                    <Badge variant="outline">Volatile Pricing</Badge>
                  )}
                  {!selectedProfile.singleSourced &&
                    !selectedProfile.criticalItem &&
                    !selectedProfile.longLeadTime &&
                    !selectedProfile.highValue &&
                    !selectedProfile.volatilePricing && (
                      <span className="text-sm text-muted-foreground">
                        No specific risk factors identified
                      </span>
                    )}
                </div>
              </div>

              {/* Mitigation */}
              {selectedProfile.mitigationStrategy && (
                <div className="space-y-2">
                  <h4 className="font-medium">Mitigation Strategy</h4>
                  <p className="text-sm text-muted-foreground">
                    {selectedProfile.mitigationStrategy}
                  </p>
                </div>
              )}

              {/* Safety Stock */}
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Safety Stock Days</span>
                <span className="font-medium">{selectedProfile.safetyStockDays} days</span>
              </div>

              {/* Notes */}
              {selectedProfile.notes && (
                <div className="space-y-2">
                  <h4 className="font-medium">Notes</h4>
                  <p className="text-sm text-muted-foreground">{selectedProfile.notes}</p>
                </div>
              )}

              {/* Last Assessed */}
              <div className="flex justify-between items-center text-sm text-muted-foreground">
                <span>Last Assessed</span>
                <span>
                  {selectedProfile.lastAssessedAt
                    ? new Date(selectedProfile.lastAssessedAt).toLocaleString()
                    : 'Never'}
                </span>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>
              Close
            </Button>
            <Button
              onClick={() => {
                setShowDetailModal(false);
                if (selectedProfile) openEditModal(selectedProfile);
              }}
            >
              Edit Profile
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
